import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChangePassword } from '@ais-ui/websockets';
import { useAuth } from '@ais-ui/auth'; // Changed from useCredentials to useAuth
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  useToast,
} from '@ais-ui/ui-core';
import React from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { mutateCallbacks } from '../../utils/mutateCallbacks';

export const changePasswordFormSchema = z
  .object({
    oldPassword: z.string().min(1),
    newPassword: z.string().min(1),
    confirmPassword: z.string().min(1),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'New password and confirmation password must match',
    path: ['confirmPassword'],
  });

export type ChangePasswordFormValues = z.infer<typeof changePasswordFormSchema>;

export const ChangePassword = () => {
  const { messages } = useLocalization();
  const { mutate } = useChangePassword();
  const { credentials, storeCredentials } = useAuth(); // Get both credentials and storeCredentials
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const { toast } = useToast();

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordFormSchema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
    },
  });

  const onSubmit = (data: ChangePasswordFormValues) => {
    const defaultCallbacks = mutateCallbacks(
      toast,
      messages,
      'change',
      'password',
      setIsSubmitting,
      undefined
    );

    const extendedCallbacks = {
      ...defaultCallbacks,
      onSuccess: () => {
        // First run the default onSuccess callback if it exists
        if (defaultCallbacks.onSuccess) {
          defaultCallbacks.onSuccess();
        }

        // Update the stored credentials with the new password
        if (credentials?.username) {
          storeCredentials({
            username: credentials.username,
            password: data.newPassword,
          });
        }
      },
    };

    mutate(
      {
        username: credentials?.username ? credentials.username : '',
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      },
      extendedCallbacks
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="oldPassword"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {messages.settingsAuthenticationChangePasswordOldLabel} *
                  </FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    className="w-96"
                    type="password"
                    aria-autocomplete="inline"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {messages.settingsAuthenticationChangePasswordNewLabel} *
                  </FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    className="w-96"
                    type="password"
                    aria-autocomplete="inline"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <div className="flex justify-between">
                  <FormLabel>
                    {
                      messages.settingsAuthenticationChangePasswordConfirmNewLabel
                    }{' '}
                    *
                  </FormLabel>
                </div>
                <FormControl onChange={() => form.clearErrors()}>
                  <Input
                    className="w-96"
                    type="password"
                    aria-autocomplete="inline"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            id="detector-confirm-delete-button"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? messages.genericSubmitting : messages.genericSubmit}
          </Button>
        </div>
      </form>
    </Form>
  );
};
